import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PulseCompanyRef } from '../../pulse-types';
import { PulseCompanyCell } from '../pulse-company-cell/PulseCompanyCell';
import styles from './PulseTables.module.scss';

export interface PulseRankedRow {
  rank: number;
  company: PulseCompanyRef;
}

export interface PulseRankedTableProps<TRow extends PulseRankedRow> {
  rows: TRow[];
  lastColumnLabel: string;
  renderLastCell: (row: TRow) => ReactNode;
  onRowClick?: (row: TRow) => void;
  boundaryAfterRowIndex?: number;
  lastCellIsNumber?: boolean;
  lastColumnAlignment?: 'left' | 'right';
  className?: string;
  'data-testid': string;
}

export const PulseRankedTable = <TRow extends PulseRankedRow>({
  rows,
  lastColumnLabel,
  renderLastCell,
  onRowClick,
  boundaryAfterRowIndex,
  lastCellIsNumber,
  lastColumnAlignment = 'right',
  className,
  'data-testid': dataTestId,
}: PulseRankedTableProps<TRow>) => {
  const { t } = useTranslation(['widget']);

  return (
    <RevampTable
      className={className}
      data-testid={dataTestId}
      withBodyRowHover={!!onRowClick}
      withBodyRowCursor={!!onRowClick}
    >
      <thead>
        <tr>
          <TableHeader
            className={styles.tableHeader}
            isHoverEnabled={false}
            alignment="start"
          >
            {t('widget:themeOverview.pulse.rank')}
          </TableHeader>
          <TableHeader
            className={styles.tableHeader}
            isHoverEnabled={false}
            alignment="start"
          >
            {t('widget:themeOverview.pulse.company')}
          </TableHeader>
          <TableHeader
            className={styles.tableHeader}
            isHoverEnabled={false}
            alignment={lastColumnAlignment === 'left' ? 'start' : 'end'}
          >
            {lastColumnLabel}
          </TableHeader>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const isSectionBoundary = index === boundaryAfterRowIndex;

          return (
            <tr
              key={row.company.entityTag}
              className={clsx({
                [styles.sectionBoundaryRow]: isSectionBoundary,
                [styles.lastRow]: index === rows.length - 1,
              })}
            >
              <TableCell
                className={clsx(styles.rankCell, {
                  [styles.sectionBoundaryCell]: isSectionBoundary,
                })}
                alignment="left"
                onClick={onRowClick && (() => onRowClick(row))}
              >
                <TopRankIcon rank={row.rank} />
              </TableCell>
              <TableCell
                className={clsx(styles.companyCell, {
                  [styles.sectionBoundaryCell]: isSectionBoundary,
                })}
                alignment="left"
                isNumber={false}
                onClick={onRowClick && (() => onRowClick(row))}
              >
                <PulseCompanyCell company={row.company} />
              </TableCell>
              <TableCell
                className={clsx(styles.valueCell, {
                  [styles.sectionBoundaryCell]: isSectionBoundary,
                })}
                alignment={lastColumnAlignment}
                isNumber={lastCellIsNumber}
                onClick={onRowClick && (() => onRowClick(row))}
              >
                {renderLastCell(row)}
              </TableCell>
            </tr>
          );
        })}
      </tbody>
    </RevampTable>
  );
};
